import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Input, Upload, Radio, Space, Button, Modal } from 'antd';
import { CloudUploadOutlined, FormOutlined, LinkOutlined, MessageOutlined, SendOutlined } from '@ant-design/icons';
import uuid from 'react-uuid';
import Utils from '../../../misc/Utils';
import Enums from '../../../misc/Enums';
import DocumentViewer from '../../../components/document-viewer/DocumentViewer';
import {
   getMicrocredentialsTaskModule,
   detectMicrocredentialsTaskMode,
   MICROCREDENTIALS_TASK_MODES,
} from '../../../config/microcredentialsTaskModules';
import '../../../pages/HomeworksPage/Components/Submissions.css';

const { TextArea } = Input;

const StudentPerformanceTaskSubmit = forwardRef((props, ref) => {
   const {
      mode = detectMicrocredentialsTaskMode(window.location?.pathname),
      canSubmit,
      remarks,
      returned,
      performanceTaskID,
      onSubmitCallback,
   } = props;

   const moduleConfig = getMicrocredentialsTaskModule(mode);
   const taskLabel = moduleConfig.label;

   const [submitType, setSubmitType] = useState('1');
   const [uploadFileList, setUploadFileList] = useState([]);
   const [message, setMessage] = useState('');
   const [urlLink, setUrlLink] = useState('');
   const [previewDocument, setPreviewDocument] = useState(null);
   const [previewDocumentName, setPreviewDocumentName] = useState(null);
   const [previewModalVisible, setPreviewModalVisible] = useState(false);
   const userId = Utils.getCurrentUserInfo(Enums.UserInfo.id);

   useEffect(() => {
      return () => {
         if (previewDocument && previewDocument.startsWith('blob:')) {
            URL.revokeObjectURL(previewDocument);
         }
      };
   }, [previewDocument]);

   const handleClosePreview = () => {
      setPreviewModalVisible(false);
      setPreviewDocument((currentDocument) => {
         if (currentDocument && currentDocument.startsWith('blob:')) {
            URL.revokeObjectURL(currentDocument);
         }

         return null;
      });
      setPreviewDocumentName(null);
   };

   const handlePreviewAttached = (file) => {
      const sourceFile = file.originFileObj || file;
      const objectUrl = URL.createObjectURL(sourceFile);

      if (previewDocument && previewDocument.startsWith('blob:')) {
         URL.revokeObjectURL(previewDocument);
      }

      setPreviewDocument(objectUrl);
      setPreviewDocumentName(file.name || sourceFile.name);
      setPreviewModalVisible(true);
   };

   const uploadProps = {
      onRemove: (file) => {
         if (previewDocumentName === file.name) {
            handleClosePreview();
         }

         setUploadFileList((currentFiles) => currentFiles.filter((currentFile) => currentFile.uid !== file.uid));
      },
      beforeUpload: (file) => {
         const isPNG = file.type === 'image/png';
         const isJPG = file.type === 'image/jpg' || file.type === 'image/jpeg';
         const isBMP = file.type === 'image/bmp';
         const isDOC = file.type === 'application/msword' || file.type === 'application/vnd.ms-word';
         const isDOCX = file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
         const isODT = file.type === 'application/vnd.oasis.opendocument.text';
         const isRTF = file.type === 'application/rtf' || file.type === 'text/rtf';
         const isTXT = file.type === 'text/plain';
         const isMD = file.type === 'text/markdown';
         const isHTML = file.type === 'text/html';
         const isXML = file.type === 'application/xml' || file.type === 'text/xml';
         const isJSON = file.type === 'application/json' || file.type === 'text/json';
         const isCSV = file.type === 'text/csv' || file.type === 'application/csv';
         const isXLS = file.type === 'application/msexcel' || file.type === 'application/vnd.ms-excel';
         const isXLSX = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
         const isPPT = file.type === 'application/vnd.ms-powerpoint';
         const isPPTX = file.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
         const isPPSX = file.type === 'application/vnd.openxmlformats-officedocument.presentationml.slideshow';
         const isPDF = file.type === 'application/pdf';
         const isMP4 = file.type === 'video/mp4';
         const isAVI = file.type === 'video/avi';
         const isMP3 = file.type === 'audio/mpeg';
         const isWAV = file.type === 'audio/wav';

         const fileValid = isPDF || isDOC || isDOCX || isODT || isRTF || isTXT || isMD || isHTML || isXML || isJSON || isCSV || isXLS || isXLSX || isPPT || isPPTX || isPPSX || isMP4 || isAVI || isMP3 || isWAV || isPNG || isJPG || isBMP;

         if (fileValid) {
            const allowedFileSize = 100;
            const isLtMB = file.size / 1024 / 1024 <= allowedFileSize;

            if (isLtMB) {
               setUploadFileList((currentFiles) => [...currentFiles, file]);
            } else {
               Modal.error({
                  title: taskLabel,
                  content: `Maximum allowed file size is ${allowedFileSize} MB`,
                  centered: true,
               });

               return Upload.LIST_IGNORE;
            }
         } else {
            Modal.error({
               title: taskLabel,
               content: 'You are not allowed to upload that file type',
               centered: true,
            });
         }

         return false;
      },
      fileList: uploadFileList,
      onPreview: (file) => {
         handlePreviewAttached(file);
      },
      showUploadList: {
         showPreviewIcon: true,
         showRemoveIcon: true,
      },
   };

   const handleSubmitPerformanceTask = () => {
      if (message && (uploadFileList.length > 0 || urlLink !== '')) {
         const o2oMode = Utils.determineOnlineOffline();
         const submitId = `submit_performance_task_${o2oMode}_${uuid()}`;
         const submittedData = {
            id: submitId,
            performance_task_id: performanceTaskID,
            student_id: userId,
            message,
            document: '',
            file_name: '',
            url_link: urlLink,
            Task_type: moduleConfig.taskType,
         };

         onSubmitCallback(submittedData, uploadFileList);
      } else {
         Modal.info({
            title: taskLabel,
            content: (
               <>
                  Please complete the following before submitting:
                  <ul>
                     <li>Message</li>
                     <li>Upload File or External link</li>
                  </ul>
               </>
            ),
            centered: true,
         });
      }
   };

   useImperativeHandle(ref, () => ({
      submitHomework: handleSubmitPerformanceTask,
   }));

   return (
      <>
         <div className="homework-panel-header">
            <div>
               <span className="homework-panel-kicker">Response</span>
               <h3>Submit {taskLabel}</h3>
            </div>
            <SendOutlined />
         </div>

         {returned &&
            <div className="homework-field-block homework-returned-block">
               <label>Return Remarks</label>
               <TextArea disabled={true} rows={3} value={remarks} />
            </div>}

         <div className="homework-field-block">
            <label>
               <MessageOutlined />
               Message
            </label>
            <TextArea
               disabled={!canSubmit}
               allowClear
               rows={returned ? 3 : 5}
               onBlur={(event) => setMessage(event.target.value)}
            />
         </div>

         <div className="homework-field-block">
            <label>
               <FormOutlined />
               Submit Method
            </label>
            <Radio.Group
               className="homework-submit-method"
               disabled={!canSubmit}
               name="submittype"
               defaultValue="1"
               buttonStyle="solid"
               onChange={(e) => setSubmitType(e.target.value)}
            >
               <Radio.Button value="0">External Link</Radio.Button>
               <Radio.Button value="1">Upload File</Radio.Button>
            </Radio.Group>
         </div>

         <div className="homework-field-block">
            {submitType === '1'
               ? (
                  <>
                     <label>
                        <CloudUploadOutlined />
                        Upload {taskLabel}
                     </label>
                     <div className="homework-upload-scroll" style={{ maxHeight: 250 }}>
                        <Upload
                           disabled={!canSubmit}
                           style={{ display: 'block' }}
                           maxCount={10}
                           multiple
                           {...uploadProps}
                           listType="picture"
                           className="upload-list-inline"
                           accept=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,
                                application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,
                                application/vnd.openxmlformats-officedocument.presentationml.slideshow,video/mp4,video/avi,audio/mpeg,audio/wav,
                                .xls,.xlsx,.png,.jpg,.pdf,.ppsx,.ppt,.pptx,"
                        >
                           <Button icon={<CloudUploadOutlined />} block>Select Files (Max: 10)</Button>
                        </Upload>
                     </div>
                  </>
               )
               : (
                  <>
                     <label>
                        <LinkOutlined />
                        External Link
                     </label>
                     <Input
                        allowClear
                        className="homework-link-input"
                        onBlur={(e) => setUrlLink(e.target.value)}
                        disabled={!canSubmit}
                     />
                  </>
               )}
         </div>

         {props.showInlineActions !== false &&
            <div className="homework-submit-actions">
               <Space direction="horizontal" className="homework-submit-action-space">
                  <Button type="primary" icon={<SendOutlined />} onClick={() => { handleSubmitPerformanceTask(); }} disabled={!canSubmit}>Submit</Button>
               </Space>
            </div>}

         <Modal
            title="Document Preview"
            centered
            destroyOnClose={true}
            open={previewModalVisible}
            width="80%"
            footer={[
               <Button key="close" shape="round" onClick={() => handleClosePreview()}>
                  Close
               </Button>,
            ]}
            onCancel={() => handleClosePreview()}
         >
            <div className="modal-document-viewer homework-document-preview-viewer">
               <DocumentViewer
                  document={previewDocument}
                  documentname={previewDocumentName}
                  dvheight="calc(100dvh - 220px)"
                  ismodal={true}
               />
            </div>
         </Modal>
      </>
   );
});

export default StudentPerformanceTaskSubmit;
